import {html, type TemplateResult} from 'lit';
import type {Option} from '../types';

/**
 * 選択肢ボタン。
 *
 * 回答直後の 1.3 秒は入力を受け付けないが、`disabled` ではなく `aria-disabled` を使う。
 * disabled にするとフォーカス中のボタンからフォーカスが外れて body に飛び、
 * キーボード操作の位置を見失うため。実際の無視はコントローラ側で行う。
 *
 * reversed（読みから字を選ぶ向き）では文字のほうを見せる。値（romanized）と並びは同じで、
 * 押したときにコントローラへ渡すものも変わらない。文字は text-3xl（弁別すべきマートラーが
 * 24px では読めない）だが leading-7 でボタンの高さ（52px）を通常の向きと揃える。
 * はみ出す上下のマートラーを切らないよう、overflow-hidden は塗りの span を包む側に持たせる
 */
export function optionButtons(
  options: Option[],
  locked: boolean,
  onSelect: (romanized: string) => void,
  reversed = false
): TemplateResult {
  return html`
    <div
      class="flex flex-wrap justify-center gap-3 mt-8"
      role="group"
      aria-label=${reversed ? 'デーヴァナーガリー文字の選択肢' : 'ローマ字表記の選択肢'}
    >
      ${options.map(
        (option) => html`
          <button
            type="button"
            part="option"
            lang=${reversed ? 'sa-Deva' : 'sa-Latn'}
            aria-disabled=${locked ? 'true' : 'false'}
            @click=${() => onSelect(option.romanized)}
            class="relative px-5 py-3 min-w-28 bg-white border-2 border-indigo-500 hover:border-indigo-700 active:border-indigo-800 rounded-lg shadow-sm hover:shadow active:shadow-none transition-[border-color,box-shadow] duration-200 ${reversed
              ? 'font-devanagari text-3xl leading-7'
              : 'text-lg'} font-medium text-indigo-800 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2 aria-disabled:opacity-50 aria-disabled:pointer-events-none"
          >
            <!-- 押下色はホバーの塗り（この span）より上に来ないと見えないので、span 側で濃くする -->
            <span class="absolute inset-0 overflow-hidden rounded-md" aria-hidden="true">
              <span
                class="absolute inset-0 bg-indigo-100 group-active:bg-indigo-200 transform scale-x-0 group-hover:scale-x-100 group-active:scale-x-100 origin-left transition-transform duration-200"
              ></span>
            </span>
            <span class="relative">${reversed ? option.char : option.romanized}</span>
          </button>
        `
      )}
    </div>
  `;
}
